/**
 * BuilderV2 Skeleton Components
 *
 * Colocated skeleton components for BuilderV2 pages.
 * These match the layout structure of actual BuilderV2 components.
 */
import { Skeleton } from '@execonline-inc/execonline-ui';
import { observer } from 'mobx-react';

// ============================================================================
// Header Skeleton - matches BuilderV2 header with title, internal name, actions
// ============================================================================
export const HeaderSkeleton = observer(function HeaderSkeleton() {
  return (
    <div className="bg-card border-b border-border">
      <div className="max-w-clientPortalMaxWidth mx-auto px-6 md:px-14 py-5">
        <div className="flex justify-between">
          <div className="flex flex-col w-1/2">
            {/* Title skeleton */}
            <div className="flex items-center mb-1">
              <Skeleton className="w-64" variant="text" size="lg" />
            </div>
            {/* Internal name skeleton */}
            <div className="flex flex-row items-center w-2/3 h-[25px] mt-2">
              <Skeleton className="w-32" variant="text" size="sm" />
            </div>
          </div>
          {/* Action buttons skeleton */}
          <div className="flex items-center gap-4">
            <Skeleton className="w-16" variant="text" size="md" />
            <Skeleton className="w-36 h-10" variant="rectangle" />
          </div>
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// Stats Bar Skeleton - matches the teal stats bar
// ============================================================================
export const StatsSkeleton = observer(function StatsSkeleton() {
  return (
    <div className="bg-primary text-white">
      <div className="max-w-clientPortalMaxWidth mx-auto px-6 md:px-14 py-5">
        <div className="flex items-center gap-4">
          <Skeleton className="w-20 !bg-primary/70" variant="text" size="sm" />
          <span className="text-primary/70">•</span>
          <Skeleton className="w-24 !bg-primary/70" variant="text" size="sm" />
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// Details Skeleton - matches title/description area with org logo
// ============================================================================
export const DetailsSkeleton = observer(function DetailsSkeleton() {
  return (
    <div className="flex pb-10 justify-between">
      {/* Left side: Title and description */}
      <div className="md:mr-11 w-3/5">
        {/* Title skeleton - matches the large editable title */}
        <Skeleton className="w-4/5 h-10" variant="text" size="xl" />
        {/* Description skeleton */}
        <div className="mt-4">
          <Skeleton className="w-32" variant="text" size="sm" />
        </div>
      </div>
      {/* Right side: Organization logo placeholder */}
      <div className="w-44 h-28 border border-dashed border-border rounded-lg flex flex-col items-center justify-center">
        <Skeleton className="w-8 h-8 mb-2" variant="rectangle" />
        <Skeleton className="w-28" variant="text" size="xs" />
        <Skeleton className="w-24" variant="text" size="xs" />
      </div>
    </div>
  );
});

// ============================================================================
// Filters Skeleton - matches search and filter dropdowns
// ============================================================================
export const FiltersSkeleton = observer(function FiltersSkeleton() {
  return (
    <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap mt-6">
      {/* Search input skeleton */}
      <Skeleton className="w-full lg:w-64 h-10" variant="rectangle" />
      {/* Filter dropdowns skeleton */}
      <div className="flex gap-2 w-full lg:w-auto">
        <Skeleton className="w-28 h-10" variant="rectangle" />
        <Skeleton className="w-36 h-10" variant="rectangle" />
        <Skeleton className="w-24 h-10" variant="rectangle" />
        <Skeleton className="w-28 h-10" variant="rectangle" />
      </div>
    </div>
  );
});

// ============================================================================
// Empty State Skeleton - matches the "Add your first section" state
// ============================================================================
export const EmptyStateSkeleton = observer(function EmptyStateSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-6 mt-8">
      <div
        className="bg-card rounded-lg py-10 px-4"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='8' ry='8' stroke='%23D6D6D6' stroke-width='2' stroke-dasharray='12%2c 8' stroke-dashoffset='0' stroke-linecap='round'/%3e%3c/svg%3e")`,
        }}
      >
        <div className="flex flex-col items-center justify-center">
          {/* Illustration placeholder */}
          <Skeleton className="w-32 h-28 mb-4 hidden md:block" variant="rectangle" />
          {/* Title text skeleton */}
          <Skeleton className="w-56 mb-1" variant="text" size="lg" />
          {/* Description text skeleton */}
          <Skeleton className="w-96 max-w-full mb-2" variant="text" size="sm" />
          <Skeleton className="w-72 max-w-full mb-6" variant="text" size="sm" />
          {/* Add section button skeleton */}
          <Skeleton className="w-32 h-10 !rounded-full" variant="rectangle" />
        </div>
      </div>
    </div>
  );
});

// ============================================================================
// Program Card Skeleton - matches individual program cards
// ============================================================================
export const ProgramCardSkeleton = observer(function ProgramCardSkeleton() {
  return (
    <div className="flex flex-col">
      {/* Image placeholder */}
      <Skeleton className="w-full h-40 !rounded-t-lg !rounded-b-none" variant="rectangle" />
      {/* Content */}
      <div className="p-4 bg-card border border-t-0 border-border rounded-b-lg">
        <Skeleton className="w-4/5 mb-2" variant="text" size="md" />
        <Skeleton className="w-2/3 mb-3" variant="text" size="sm" />
        <Skeleton className="w-32 h-6 !rounded-full" variant="rectangle" />
      </div>
    </div>
  );
});

// ============================================================================
// Section Skeleton - matches a full section with header and program cards
// ============================================================================
export const SectionSkeleton = observer(function SectionSkeleton() {
  return (
    <div className="max-w-7xl mx-auto px-6">
      <div className="bg-card rounded-lg shadow-section p-6">
        {/* Section header */}
        <div className="flex justify-between items-start mb-4">
          <div className="flex-1">
            <Skeleton className="w-64 mb-2" variant="text" size="lg" />
            <Skeleton className="w-96 max-w-full" variant="text" size="sm" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="w-28 h-9 !rounded-full" variant="rectangle" />
            <Skeleton className="w-9 h-9" variant="circle" />
            <Skeleton className="w-9 h-9" variant="circle" />
          </div>
        </div>
        {/* Program cards grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mt-6">
          <ProgramCardSkeleton />
          <ProgramCardSkeleton />
          <ProgramCardSkeleton />
        </div>
      </div>
    </div>
  );
});
